import { z } from 'zod';
import { financeExpenseCategorySchema, financeExpenseIdSchema, financeExpenseScopeSchema, } from './finance-expense.contract.js';
import { financeDateOnlySchema, financeMoneyCentsSchema } from './finance-invoice.contract.js';
export const financeOverheadCapability = 'finance.overhead.v1';
export const financeOverheadIdSchema = z.string().uuid();
export const financeOverheadRevisionSchema = z.number().int().positive();
export const financeOverheadCodeSchema = z.string().trim().min(1).max(50)
    .transform((value) => value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))
    .refine((value) => /^[A-Z0-9][A-Z0-9_]{0,49}$/.test(value));
export const financeOverheadAllocationBasisSchema = z.enum([
    'revenue', 'labor-hours', 'project-days', 'active-project-count', 'manual', 'none',
]);
export const financeOverheadEntityTypeSchema = z.enum([
    'cost-center', 'account-category', 'bill', 'bill-occurrence', 'allocation-policy',
]);
const nullableText = (maximum) => z.string().trim().min(1).max(maximum).nullable();
const entityEnvelopeShape = {
    tenantId: z.string().uuid(),
    id: financeOverheadIdSchema,
    revision: financeOverheadRevisionSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
};
export const financeOverheadCostCenterTypeSchema = z.enum([
    'project', 'shop', 'fleet', 'office', 'warehouse', 'administration', 'sales', 'owner',
    'overhead', 'other',
]);
export const financeOverheadCostCenterValueSchema = z.strictObject({
    code: financeOverheadCodeSchema,
    name: z.string().trim().min(1).max(200),
    description: nullableText(1_000).default(null),
    type: financeOverheadCostCenterTypeSchema,
    defaultExpenseCategory: financeExpenseCategorySchema.nullable().default(null),
    allocatable: z.boolean().default(false),
    allocationBasis: financeOverheadAllocationBasisSchema.default('none'),
    active: z.boolean().default(true),
}).superRefine((value, context) => {
    if (!value.allocatable && value.allocationBasis !== 'none') {
        context.addIssue({
            code: 'custom',
            path: ['allocationBasis'],
            message: 'A nonallocatable cost center must use the none allocation basis.',
        });
    }
    if (value.allocatable && value.allocationBasis === 'none') {
        context.addIssue({
            code: 'custom',
            path: ['allocationBasis'],
            message: 'An allocatable cost center requires an allocation basis.',
        });
    }
});
export const financeOverheadCostCenterSchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    entityType: z.literal('cost-center'),
    ...entityEnvelopeShape,
    value: financeOverheadCostCenterValueSchema,
});
export const financeOverheadAccountTypeSchema = z.enum([
    'income', 'project-expense', 'overhead', 'payroll', 'commission', 'asset', 'liability',
    'equity', 'other',
]);
export const financeOverheadPostingBehaviorSchema = z.enum(['posting', 'reporting-only']);
export const financeOverheadAccountCategoryValueSchema = z.strictObject({
    code: financeOverheadCodeSchema,
    name: z.string().trim().min(1).max(200),
    description: nullableText(1_000).default(null),
    accountType: financeOverheadAccountTypeSchema,
    defaultExpenseCategory: financeExpenseCategorySchema.nullable().default(null),
    expenseScope: financeExpenseScopeSchema.nullable().default(null),
    postingBehavior: financeOverheadPostingBehaviorSchema.default('posting'),
    active: z.boolean().default(true),
    systemDefault: z.boolean().default(false),
});
export const financeOverheadAccountCategorySchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    entityType: z.literal('account-category'),
    ...entityEnvelopeShape,
    value: financeOverheadAccountCategoryValueSchema,
});
export const financeOverheadBillCategorySchema = z.enum([
    'rent', 'utilities', 'internet', 'insurance', 'software', 'loan', 'vehicle', 'tax',
    'payroll-related', 'other',
]);
export const financeOverheadAmountModeSchema = z.enum(['fixed', 'variable', 'estimate']);
export const financeOverheadCadenceSchema = z.enum([
    'weekly', 'biweekly', 'monthly', 'quarterly', 'annual', 'custom',
]);
export const financeOverheadBillValueSchema = z.strictObject({
    vendor: z.string().trim().min(1).max(300),
    name: z.string().trim().min(1).max(300),
    description: nullableText(2_000).default(null),
    category: financeOverheadBillCategorySchema,
    costCenterId: financeOverheadIdSchema.nullable().default(null),
    accountCategoryId: financeOverheadIdSchema.nullable().default(null),
    amountMode: financeOverheadAmountModeSchema,
    defaultAmountCents: financeMoneyCentsSchema.nullable().default(null),
    currencyCode: z.literal('USD'),
    cadence: financeOverheadCadenceSchema,
    dueDay: z.number().int().min(1).max(31).nullable().default(null),
    nextDueDate: financeDateOnlySchema.nullable().default(null),
    paymentMethod: nullableText(100).default(null),
    autopay: z.boolean().default(false),
    active: z.boolean().default(true),
    allocationEligible: z.boolean().default(true),
    allocationBasis: financeOverheadAllocationBasisSchema.default('revenue'),
    notes: nullableText(2_000).default(null),
}).superRefine((value, context) => {
    if (value.amountMode === 'fixed' && value.defaultAmountCents === null) {
        context.addIssue({
            code: 'custom',
            path: ['defaultAmountCents'],
            message: 'A fixed recurring bill requires its default amount.',
        });
    }
    if (!value.allocationEligible && value.allocationBasis !== 'none') {
        context.addIssue({
            code: 'custom',
            path: ['allocationBasis'],
            message: 'A nonallocatable bill must use the none allocation basis.',
        });
    }
});
export const financeOverheadBillSchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    entityType: z.literal('bill'),
    ...entityEnvelopeShape,
    value: financeOverheadBillValueSchema,
});
export const financeOverheadOccurrenceStatusSchema = z.enum([
    'scheduled', 'uploaded', 'approved', 'paid', 'late', 'skipped', 'cancelled',
]);
export const financeOverheadBillOccurrenceValueSchema = z.strictObject({
    billId: financeOverheadIdSchema,
    periodStart: financeDateOnlySchema.nullable().default(null),
    periodEnd: financeDateOnlySchema.nullable().default(null),
    dueDate: financeDateOnlySchema,
    expectedAmountCents: financeMoneyCentsSchema.nullable().default(null),
    actualAmountCents: financeMoneyCentsSchema.nullable().default(null),
    status: financeOverheadOccurrenceStatusSchema,
    expenseId: financeExpenseIdSchema.nullable().default(null),
    paidAt: z.string().datetime({ offset: true }).nullable().default(null),
    notes: nullableText(2_000).default(null),
}).superRefine((value, context) => {
    if (value.periodStart !== null && value.periodEnd !== null && value.periodEnd < value.periodStart) {
        context.addIssue({
            code: 'custom',
            path: ['periodEnd'],
            message: 'An occurrence period cannot end before it starts.',
        });
    }
    const paidEvidenceComplete = value.actualAmountCents !== null && value.paidAt !== null;
    if ((value.status === 'paid') !== paidEvidenceComplete) {
        context.addIssue({
            code: 'custom',
            path: ['paidAt'],
            message: 'Paid occurrence state requires exact actual amount and paid time evidence.',
        });
    }
});
export const financeOverheadBillOccurrenceSchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    entityType: z.literal('bill-occurrence'),
    ...entityEnvelopeShape,
    value: financeOverheadBillOccurrenceValueSchema,
});
export const financeOverheadAllocationPolicyValueSchema = z.strictObject({
    enabled: z.boolean(),
    annualOverheadCents: financeMoneyCentsSchema,
    annualRevenueBasisCents: financeMoneyCentsSchema,
    explicitRatePartsPerMillion: z.number().int().min(0).max(1_000_000).nullable(),
    includeDraftInvoices: z.boolean(),
    defaultRunStatus: z.enum(['draft', 'locked']),
});
export const financeOverheadAllocationPolicySchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    entityType: z.literal('allocation-policy'),
    ...entityEnvelopeShape,
    value: financeOverheadAllocationPolicyValueSchema,
});
const overheadCommandEnvelopeShape = {
    commandId: z.string().uuid(),
    entityId: financeOverheadIdSchema,
    expectedRevision: z.number().int().min(0),
    reason: z.string().trim().min(1).max(2_000),
};
export const financeOverheadCostCenterCommandSchema = z.strictObject({
    entityType: z.literal('cost-center'),
    ...overheadCommandEnvelopeShape,
    value: financeOverheadCostCenterValueSchema,
});
export const financeOverheadAccountCategoryCommandSchema = z.strictObject({
    entityType: z.literal('account-category'),
    ...overheadCommandEnvelopeShape,
    value: financeOverheadAccountCategoryValueSchema,
});
export const financeOverheadBillCommandSchema = z.strictObject({
    entityType: z.literal('bill'),
    ...overheadCommandEnvelopeShape,
    value: financeOverheadBillValueSchema,
});
export const financeOverheadBillOccurrenceCommandSchema = z.strictObject({
    entityType: z.literal('bill-occurrence'),
    ...overheadCommandEnvelopeShape,
    value: financeOverheadBillOccurrenceValueSchema,
});
export const financeOverheadAllocationPolicyCommandSchema = z.strictObject({
    entityType: z.literal('allocation-policy'),
    ...overheadCommandEnvelopeShape,
    value: financeOverheadAllocationPolicyValueSchema,
});
export const financeOverheadEntityCommandSchema = z.discriminatedUnion('entityType', [
    financeOverheadCostCenterCommandSchema,
    financeOverheadAccountCategoryCommandSchema,
    financeOverheadBillCommandSchema,
    financeOverheadBillOccurrenceCommandSchema,
    financeOverheadAllocationPolicyCommandSchema,
]);
export const financeOverheadEntitySchema = z.discriminatedUnion('entityType', [
    financeOverheadCostCenterSchema,
    financeOverheadAccountCategorySchema,
    financeOverheadBillSchema,
    financeOverheadBillOccurrenceSchema,
    financeOverheadAllocationPolicySchema,
]);
export const financeOverheadEntityCommandResponseSchema = z.strictObject({
    data: financeOverheadEntitySchema,
    command: z.strictObject({
        commandId: z.string().uuid(),
        idempotent: z.boolean(),
    }),
});
export const financeOverheadListQuerySchema = z.strictObject({
    active: z.boolean().optional(),
    status: financeOverheadOccurrenceStatusSchema.optional(),
    billId: financeOverheadIdSchema.optional(),
    dateFrom: financeDateOnlySchema.optional(),
    dateTo: financeDateOnlySchema.optional(),
    limit: z.number().int().min(1).max(250).default(100),
    offset: z.number().int().min(0).max(1_000_000).default(0),
});
const financeOverheadPageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
function pagedSchema(itemSchema) {
    return z.strictObject({ data: z.array(itemSchema), page: financeOverheadPageSchema })
        .superRefine((response, context) => {
        if (response.page.returned !== response.data.length) {
            context.addIssue({
                code: 'custom', path: ['page', 'returned'], message: 'Page count must match returned data.',
            });
        }
    });
}
export const financeOverheadCostCenterListResponseSchema = pagedSchema(financeOverheadCostCenterSchema);
export const financeOverheadAccountCategoryListResponseSchema = pagedSchema(financeOverheadAccountCategorySchema);
export const financeOverheadBillListResponseSchema = pagedSchema(financeOverheadBillSchema);
export const financeOverheadBillOccurrenceListResponseSchema = pagedSchema(financeOverheadBillOccurrenceSchema);
export const financeOverheadAllocationItemSchema = z.strictObject({
    projectId: z.string().uuid(),
    projectNumber: z.string().trim().min(1).max(80),
    projectName: z.string().trim().min(1).max(240),
    basisCents: financeMoneyCentsSchema,
    allocatedCents: financeMoneyCentsSchema,
});
export const financeOverheadAllocationPreviewSchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    periodStart: financeDateOnlySchema,
    periodEnd: financeDateOnlySchema,
    allocationRatePartsPerMillion: z.number().int().min(0).max(1_000_000),
    includeDraftInvoices: z.boolean(),
    totalBasisCents: financeMoneyCentsSchema,
    totalAllocatedCents: financeMoneyCentsSchema,
    items: z.array(financeOverheadAllocationItemSchema).max(100_000),
}).superRefine((preview, context) => {
    const basis = preview.items.reduce((sum, item) => sum + item.basisCents, 0);
    const allocated = preview.items.reduce((sum, item) => sum + item.allocatedCents, 0);
    if (basis !== preview.totalBasisCents) {
        context.addIssue({ code: 'custom', path: ['totalBasisCents'], message: 'Basis total mismatch.' });
    }
    if (allocated !== preview.totalAllocatedCents) {
        context.addIssue({
            code: 'custom', path: ['totalAllocatedCents'], message: 'Allocation total mismatch.',
        });
    }
});
export const financeOverheadAllocationPreviewRequestSchema = z.strictObject({
    periodStart: financeDateOnlySchema,
    periodEnd: financeDateOnlySchema,
}).superRefine((value, context) => {
    if (value.periodEnd < value.periodStart) {
        context.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Period order is invalid.' });
    }
});
export const financeOverheadAllocationRunRequestSchema = z.strictObject({
    commandId: z.string().uuid(),
    runId: financeOverheadIdSchema,
    periodStart: financeDateOnlySchema,
    periodEnd: financeDateOnlySchema,
    status: z.enum(['draft', 'locked']),
    reason: z.string().trim().min(1).max(2_000),
}).superRefine((value, context) => {
    if (value.periodEnd < value.periodStart) {
        context.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Period order is invalid.' });
    }
});
export const financeOverheadAllocationRunSchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    id: financeOverheadIdSchema,
    tenantId: z.string().uuid(),
    revision: financeOverheadRevisionSchema,
    periodStart: financeDateOnlySchema,
    periodEnd: financeDateOnlySchema,
    status: z.enum(['draft', 'locked', 'superseded']),
    allocationBasis: z.literal('revenue'),
    allocationRatePartsPerMillion: z.number().int().min(0).max(1_000_000),
    includeDraftInvoices: z.boolean(),
    totalBasisCents: financeMoneyCentsSchema,
    totalAllocatedCents: financeMoneyCentsSchema,
    policyId: financeOverheadIdSchema,
    policyRevision: financeOverheadRevisionSchema,
    reason: z.string().trim().min(1).max(2_000),
    createdByMembershipId: z.string().uuid(),
    lockedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    items: z.array(financeOverheadAllocationItemSchema).max(100_000),
});
export const financeOverheadAllocationRunResponseSchema = z.strictObject({
    data: financeOverheadAllocationRunSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }),
});
export const financeOverheadAllocationRunListResponseSchema = pagedSchema(financeOverheadAllocationRunSchema);
const financeOverheadOccurrenceSummarySchema = z.strictObject({
    id: financeOverheadIdSchema,
    billId: financeOverheadIdSchema,
    billName: z.string().trim().min(1).max(300),
    vendor: z.string().trim().min(1).max(300),
    dueDate: financeDateOnlySchema,
    status: financeOverheadOccurrenceStatusSchema,
    expectedAmountCents: financeMoneyCentsSchema.nullable(),
    actualAmountCents: financeMoneyCentsSchema.nullable(),
});
export const financeOverheadDashboardSchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    window: z.strictObject({ dateFrom: financeDateOnlySchema, dateTo: financeDateOnlySchema }),
    expenses: z.strictObject({
        approvedCents: financeMoneyCentsSchema,
        pendingCents: financeMoneyCentsSchema,
        approvedCount: z.number().int().nonnegative(),
        pendingCount: z.number().int().nonnegative(),
    }),
    bills: z.strictObject({
        expectedCents: financeMoneyCentsSchema,
        actualCents: financeMoneyCentsSchema,
        occurrenceCount: z.number().int().nonnegative(),
        overdueCount: z.number().int().nonnegative(),
        upcomingCount: z.number().int().nonnegative(),
        overdue: z.array(financeOverheadOccurrenceSummarySchema).max(25),
        upcoming: z.array(financeOverheadOccurrenceSummarySchema).max(25),
    }),
});
export const financeOverheadForecastSchema = z.strictObject({
    contractVersion: z.literal(financeOverheadCapability),
    window: z.strictObject({ dateFrom: financeDateOnlySchema, dateTo: financeDateOnlySchema }),
    totalExpectedCents: financeMoneyCentsSchema,
    totalActualCents: financeMoneyCentsSchema,
    byWeek: z.array(z.strictObject({
        weekStart: financeDateOnlySchema,
        expectedCents: financeMoneyCentsSchema,
        actualCents: financeMoneyCentsSchema,
        count: z.number().int().nonnegative(),
    })).max(60),
    occurrences: z.array(financeOverheadOccurrenceSummarySchema).max(1_000),
});
export function computeFinanceOverheadRatePartsPerMillion(policy) {
    if (!policy.enabled)
        return 0;
    if (policy.explicitRatePartsPerMillion !== null)
        return policy.explicitRatePartsPerMillion;
    if (policy.annualRevenueBasisCents === 0)
        return 0;
    const numerator = BigInt(policy.annualOverheadCents) * 1000000n;
    const denominator = BigInt(policy.annualRevenueBasisCents);
    const rounded = (numerator + denominator / 2n) / denominator;
    return Number(rounded > 1000000n ? 1000000n : rounded);
}
//# sourceMappingURL=finance-overhead.contract.js.map